import { useEffect, useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { ApiError } from '@/deploy/services/api';
import { projectsService } from '@/deploy/services/projects.service';
import type { ProjectGroup } from '@/deploy/types';
import { apiGroupLabel } from '@/deploy/utils/apiEnvs';
import { NONE_INSTANCE_LABEL, instanceErrorMessage, isNoneInstance } from '@/deploy/utils/apiInstances';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';
import styles from './api/ApiInstances.module.scss';

// ============================================================
// Groupes de jeu du projet (Nova, Iris...) : un groupe = les serveurs de jeu dont la variable
// Pterodactyl RECUBE_GROUP vaut sa clé. La clé est immuable une fois enregistrée (paramètre
// public ?group=, répartition des APIs) ; libellé et couleur changent librement.
// ============================================================

const GROUP_KEY = /^[a-z][a-z0-9-]{0,15}$/;
const RESERVED = ['default', 'commune', 'all', 'tous'];
const MAX_GROUPS = 10;
const LABEL_MAX = 32;
const PALETTE = ['#22C55E', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#14B8A6', '#EF4444', '#6366F1', '#84CC16', '#F97316'];

interface Row extends ProjectGroup {
  /** Déjà enregistré : clé verrouillée. */
  saved: boolean;
  /** Clé saisie à la main : elle ne suit plus le libellé. */
  keyTouched: boolean;
}

interface UsedBy { apiName: string; environment: string; group: string; instanceLabel?: string; instance: string }

/** Clé proposée depuis le libellé : minuscules sans accents, tirets, 16 caractères. */
function keyFromLabel(label: string): string {
  const k = label.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^[^a-z]+/, '').replace(/-+$/, '');
  return k.slice(0, 16).replace(/-+$/, '');
}

function rowError(row: Row, rows: Row[]): string | null {
  if (!GROUP_KEY.test(row.key)) return 'Clé : minuscule en tête, puis minuscules, chiffres ou -, 16 caractères au plus';
  if (RESERVED.includes(row.key)) return `Clé réservée : ${row.key}`;
  if (rows.filter((r) => r.key === row.key).length > 1) return `Clé en double : ${row.key}`;
  const l = row.label.trim();
  if (!l || l.length > LABEL_MAX) return `Libellé : 1 à ${LABEL_MAX} caractères`;
  return null;
}

export function ProjectGroupsModal({ projectId, projectName, onClose, onSaved }: {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSaved: (groups: ProjectGroup[]) => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [initial, setInitial] = useState<ProjectGroup[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const list = await projectsService.getGroups(projectId);
        setInitial(list);
        setRows(list.map((g) => ({ ...g, saved: true, keyTouched: true })));
      } catch (e) {
        setLoadError(instanceErrorMessage(e));
      }
    })();
  }, [projectId]);

  const update = (i: number, patch: Partial<Row>) => setRows((rs) => rs && rs.map((r, j) => {
    if (j !== i) return r;
    const next = { ...r, ...patch };
    // Tant que la clé n'a pas été saisie, elle suit le libellé
    if (patch.label !== undefined && !r.saved && !r.keyTouched) next.key = keyFromLabel(patch.label);
    return next;
  }));

  const add = () => setRows((rs) => rs && [...rs, {
    key: '', label: '', color: PALETTE[rs.length % PALETTE.length], saved: false, keyTouched: false,
  }]);

  const errors = rows ? rows.map((r) => rowError(r, rows)) : [];
  const valid = !!rows && errors.every((e) => !e) && rows.length <= MAX_GROUPS;

  const save = async () => {
    if (!rows) return;
    setError(null);
    setSaving(true);
    try {
      const saved = await projectsService.setGroups(projectId, rows.map((r) => ({
        key: r.key, label: r.label.trim(), ...(r.color ? { color: r.color } : {}),
      })));
      onSaved(saved);
      onClose();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { usedBy?: UsedBy[]; message?: string } | undefined) : undefined;
      const usedBy = body?.usedBy;
      if (usedBy && usedBy.length > 0 && !body?.message) {
        // Repli sans message du CICD : même texte que lui, une phrase par groupe, « Aucune » comprise
        const byGroup = new Map<string, UsedBy[]>();
        for (const u of usedBy) byGroup.set(u.group, [...(byGroup.get(u.group) ?? []), u]);
        const labelOf = (k: string) => initial.find((g) => g.key === k)?.label ?? k;
        const sentences = [...byGroup.entries()].map(([g, uses]) => {
          const none = uses.filter((u) => isNoneInstance(u.instance)).length;
          const where = none === uses.length ? `est réglé sur « ${NONE_INSTANCE_LABEL} »`
            : none > 0 ? `est réparti vers d'autres instances ou réglé sur « ${NONE_INSTANCE_LABEL} »`
              : "est réparti vers d'autres instances";
          const parts = uses.map((u) => `${u.apiName} (${apiGroupLabel(u.environment)} → ${
            isNoneInstance(u.instance) ? NONE_INSTANCE_LABEL : u.instanceLabel ?? u.instance})`);
          return `Le groupe ${labelOf(g)} ${where} : ${parts.join(', ')}.`;
        });
        setError(`${sentences.join(' ')} Remets-${sentences.length > 1 ? 'les' : 'le'} sur la Principale dans ces répartitions d'abord.`);
      } else {
        // Le CICD compose le message (groupes, API, env, cible, marche à suivre)
        setError(instanceErrorMessage(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="Groupes de jeu"
      subtitle={`${projectName} · sous-ensembles de serveurs déclarés par RECUBE_GROUP`}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="primary" onClick={() => void save()} loading={saving} disabled={!valid || saving}>
          Enregistrer
        </Button>
      </>}
    >
      <div className={pageStyles.form}>
        {loadError && <div className={pageStyles.error}>{loadError}</div>}
        {error && <div className={pageStyles.error}>{error}</div>}
        {!rows && !loadError && <div className={pageStyles.fieldHint}>Chargement…</div>}
        {rows && (<>
          <div className={styles.groupRows}>
            <div className={styles.groupRow}>
              <span className={styles.groupHead}>Clé</span>
              <span className={styles.groupHead}>Libellé</span>
              <span className={styles.groupHead}>Couleur</span>
              <span />
            </div>
            {rows.map((r, i) => (
              <div key={i}>
                <div className={styles.groupRow}>
                  <input type="text" value={r.key} disabled={r.saved || saving} maxLength={16}
                    aria-label="Clé du groupe" placeholder="iris"
                    title={r.saved ? 'Clé immuable une fois enregistrée' : undefined}
                    onChange={(e) => update(i, { key: e.target.value.trim().toLowerCase(), keyTouched: true })} />
                  <input type="text" value={r.label} disabled={saving} maxLength={LABEL_MAX}
                    aria-label="Libellé du groupe" placeholder="Iris"
                    onChange={(e) => update(i, { label: e.target.value })} />
                  <input type="color" value={r.color ?? '#94A3B8'} disabled={saving} aria-label="Couleur du groupe"
                    onChange={(e) => update(i, { color: e.target.value.toUpperCase() })} />
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />} disabled={saving}
                    onClick={() => setRows((rs) => rs && rs.filter((_, j) => j !== i))}
                    title={`Retirer ce groupe (refusé tant qu'une répartition l'envoie ailleurs que sur la Principale ou le règle sur « ${NONE_INSTANCE_LABEL} »)`}>
                    Retirer
                  </Button>
                </div>
                {errors[i] && (r.key || r.label) && (
                  <div className={[pageStyles.fieldHint, pageStyles.fieldHintDanger].join(' ')}>{errors[i]}</div>
                )}
              </div>
            ))}
            {rows.length === 0 && (
              <div className={pageStyles.fieldHint}>Aucun groupe : tous les serveurs de jeu reçoivent les instances Principales.</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" leftIcon={<Icon name="plus" size={12} />}
              onClick={add} disabled={saving || rows.length >= MAX_GROUPS}>
              Ajouter un groupe
            </Button>
            {rows.length >= MAX_GROUPS && <span className={pageStyles.fieldHint}>{MAX_GROUPS} groupes maximum</span>}
            <span style={{ flex: 1 }} />
            {rows.filter((r) => r.label.trim()).map((r, i) => <GroupBadge key={`${r.key}-${i}`} group={r} />)}
          </div>
          <div className={pageStyles.fieldHint}>
            La clé est la valeur de RECUBE_GROUP sur les serveurs du groupe et du paramètre ?group= de l'endpoint
            public : elle ne change plus une fois enregistrée. Retirer un groupe encore réparti vers une autre
            instance que la Principale, ou réglé sur « {NONE_INSTANCE_LABEL} », est refusé : remets-le d'abord sur la
            Principale dans ces répartitions.
          </div>
        </>)}
      </div>
    </Modal>
  );
}
